import { Component, OnInit, ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import { ConfirmationService, MessageService } from 'primeng/api';
import { SwalService } from '@shared/utils/swal.service';
import { RutaCriticaService } from './services/ruta-critica.service';
import { IRutaCriticaProceso } from './interfaces/ruta-critica.interface';
import { MatDialog } from '@angular/material/dialog';
import { CopiarRutaDialogComponent } from './copiar-ruta-dialog.component';

interface Column {
  field: string;
  header: string;
}

@Component({
  selector: 'app-ruta-critica',
  templateUrl: './ruta-critica.component.html',
  providers: [ConfirmationService, MessageService]
})
export class RutaCriticaComponent implements OnInit {
  @ViewChild('dt') table!: Table;

  displayedColumns: Column[] = [
    { field: 'numero_Proceso', header: 'N° Ruta' },
    { field: 'descripcion', header: 'Descripción' },
    { field: 'descriptivo', header: 'Descriptivo' },
    { field: 'duracion', header: 'Duración' },
    { field: 'costo', header: 'Costo' },
    { field: 'actions', header: 'Acciones' }
  ];

  public loading: boolean = true;
  public dataSource: IRutaCriticaProceso[] = [];
  public globalFilter: string = '';
  public first: number = 0;
  public rows: number = 10;
  public totalRecords: number = 0;

  displayDialog: boolean = false;
  selectedProceso: IRutaCriticaProceso | null = null;

  displayCopiarDialog: boolean = false;
  copiarFormResult: any = null;

  constructor(
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private swalService: SwalService,
    private rutaCriticaService: RutaCriticaService
  ) {}

  ngOnInit(): void {
    this.read();
  }

  read() {
    this.loading = true;
    this.rutaCriticaService.getProcesos().subscribe({
      next: (response) => {
        if (response.success) {
          this.dataSource = response.data;
          this.totalRecords = response.data.length;
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudieron cargar las rutas críticas'
          });
        }
      },
      error: (error) => {
        console.error('Error al cargar rutas críticas:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar las rutas críticas'
        });
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  openCreate() {
    this.selectedProceso = null;
    this.displayDialog = true;
  }

  openUpdate(model: IRutaCriticaProceso) {
    this.selectedProceso = model;
    this.displayDialog = true;
  }

  onDialogClosed(event?: {refresh: boolean, numero_Proceso?: number, success?: boolean}) {
    this.displayDialog = false;
    if (event) {
      if (event.success === true) {
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'Ruta crítica guardada correctamente'
        });
      } else if (event.success === false) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo guardar la ruta crítica'
        });
      }
      if (event.refresh) {
        this.read();
      }
    }
  }

  readAndFilterByNumeroProceso(numero_Proceso: number) {
    this.loading = true;
    this.rutaCriticaService.getProcesos().subscribe({
      next: (response) => {
        if (response.success) {
          this.dataSource = response.data;
          this.totalRecords = response.data.length;
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'No se pudieron cargar las rutas críticas'
          });
        }
      },
      error: (error) => {
        console.error('Error al cargar rutas críticas:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar las rutas críticas'
        });
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  delete(model: IRutaCriticaProceso) {
    import('sweetalert2').then(Swal => {
      Swal.default.fire({
        title: '¿Está seguro?',
        text: `¿Está seguro de eliminar la ruta crítica ${model.numeroProceso ?? (model as any).numero_Proceso}?`,
        icon: 'warning',
        showCancelButton: true,
        iconColor: "#d33",
        confirmButtonColor: "#d33",
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'No',
        heightAuto: false
      }).then((result: any) => {
        if (result.isConfirmed) {
          this.loading = true;
          this.rutaCriticaService.eliminarProceso(model.numeroProceso ?? (model as any).numero_Proceso).subscribe({
            next: (res) => {
              if (res.success && res.data.error === '0') {
                this.globalFilter = '';
                if (this.table) this.table.clear();
                this.messageService.add({
                  severity: 'success',
                  summary: 'Éxito',
                  detail: 'Ruta crítica eliminada correctamente'
                });
                this.read();
              } else {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Error',
                  detail: 'Error al eliminar la ruta crítica'
                });
              }
            },
            error: (error) => {
              console.error('Error al eliminar ruta crítica:', error);
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Error al eliminar la ruta crítica'
              });
            },
            complete: () => {
              this.loading = false;
            }
          });
        }
      });
    });
  }

  onPage(event: any): void {
    this.first = event.first;
    this.rows = event.rows;
  }

  onFilter(event: any): void {
    this.globalFilter = event.filteredValue;
  }

  openCopiarDialog() {
    this.displayCopiarDialog = true;
  }

  onCopiarDialogClosed(result?: any) {
    this.displayCopiarDialog = false;
    if (result) {
      this.messageService.add({
        severity: result.success ? 'success' : 'error',
        summary: result.success ? 'Éxito' : 'Error',
        detail: result.mensaje
      });
      if (result.success) {
        this.read();
      }
    }
  }
} 